import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Eye, Copy, Wand2, RefreshCw, Image as ImageIcon, X, Search, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import PostService from '../services/postService';
import AIService from '../services/aiService';
import Modal from '../components/Shared/Modal';
import Button from '../components/Shared/Button';
import Toast from '../components/Shared/Toast';
import ActionMenu, { ActionMenuItem } from '../components/Shared/ActionMenu';
import ImageGenerationModal from '../components/Shared/ImageGenerationModal';
import { getStaticUrl } from '../config';

const POSTS_PER_PAGE = 10;

const SavedPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });
  const [imagePrompts, setImagePrompts] = useState([]);
  const [imagePromptsLoading, setImagePromptsLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageServiceAvailable, setImageServiceAvailable] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Search and Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadPosts();
    checkImageService();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter]);

  const checkImageService = async () => {
    try {
      const response = await AIService.checkImageServiceStatus();
      setImageServiceAvailable(response.data.available);
    } catch (error) {
      console.error('Failed to check image service:', error);
      setImageServiceAvailable(false);
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await PostService.getAllPosts();
      if (response.success) {
        setPosts(response.data.posts || []);
      } else {
        console.error('Failed to load posts:', response.error);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered and Paginated Posts
  const filteredPosts = useMemo(() => {
    let result = [...posts];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(post =>
        post.title?.toLowerCase().includes(query) ||
        post.content?.toLowerCase().includes(query)
      );
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      result = result.filter(post => new Date(post.created_at) >= filterDate);
    }

    return result;
  }, [posts, searchQuery, dateFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const handleDelete = async (postId) => {
    if (!confirm('هل أنت متأكد من حذف هذا المنشور؟')) {
      return;
    }

    try {
      const response = await PostService.deletePost(postId);
      if (response.success) {
        setPosts(posts.filter(post => post.id !== postId));
        if (selectedPost?.id === postId) {
          setSelectedPost(null);
        }
        setToast({ isVisible: true, message: 'تم حذف المنشور بنجاح', type: 'success' });
      } else {
        setToast({ isVisible: true, message: 'فشل في حذف المنشور: ' + response.error?.message, type: 'error' });
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'خطأ في حذف المنشور: ' + error.message, type: 'error' });
    }
  };

  const handleCopy = (content, hashtags = []) => {
    const fullText = hashtags && hashtags.length > 0
      ? `${content}\n\n${hashtags.join(' ')}`
      : content;
    navigator.clipboard.writeText(fullText);
    setToast({ isVisible: true, message: 'تم نسخ المنشور!', type: 'success' });
  };

  const handleViewPost = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    setFeedback('');
  };

  const handleImprovePost = async () => {
    if (!feedback.trim()) {
      setToast({ isVisible: true, message: 'الرجاء إدخال ملاحظاتك', type: 'warning' });
      return;
    }

    setIsImproving(true);
    try {
      const response = await AIService.refinePost(selectedPost.content, feedback);
      if (response.success) {
        const updatedPost = {
          ...selectedPost,
          content: response.data.refinedContent || response.data.content,
          version: selectedPost.version + 1
        };

        // Update in database
        await PostService.updatePost(selectedPost.id, {
          content: updatedPost.content,
          version: updatedPost.version
        });

        // Update local state
        setPosts(posts.map(p => p.id === selectedPost.id ? updatedPost : p));
        setSelectedPost(updatedPost);
        setFeedback('');
        setToast({ isVisible: true, message: 'تم تحسين المنشور بنجاح!', type: 'success' });
      } else {
        throw new Error(response.error?.message || 'فشل في تحسين المنشور');
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في تحسين المنشور: ' + error.message, type: 'error' });
    } finally {
      setIsImproving(false);
    }
  };

  const handleRegenerateHashtags = async () => {
    if (!selectedPost) return;

    setIsImproving(true);
    try {
      const response = await AIService.generateHashtags(selectedPost.title, selectedPost.content);

      if (response.success) {
        const hashtags = response.data.hashtags || [];

        const updatedPost = {
          ...selectedPost,
          hashtags: JSON.stringify(hashtags)
        };

        await PostService.updatePost(selectedPost.id, {
          hashtags: updatedPost.hashtags
        });

        setPosts(posts.map(p => p.id === selectedPost.id ? updatedPost : p));
        setSelectedPost(updatedPost);
        setToast({ isVisible: true, message: 'تم إعادة توليد الهاشتاقات!', type: 'success' });
      } else {
        throw new Error(response.error?.message || 'فشل في إعادة توليد الهاشتاقات');
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في إعادة توليد الهاشتاقات: ' + error.message, type: 'error' });
    } finally {
      setIsImproving(false);
    }
  };

  const handleGenerateImagePrompts = async () => {
    if (!selectedPost) return;

    setImagePromptsLoading(true);
    try {
      const response = await AIService.generateImagePrompts({
        postId: selectedPost.id,
        content: selectedPost.content
      });

      if (response.success && response.data.prompts) {
        const promptTexts = response.data.prompts.map(p => p.prompt);

        if (promptTexts.length > 0) {
          setImagePrompts(promptTexts);
          setToast({ isVisible: true, message: 'تم إنشاء وصف الصور!', type: 'success' });
        }
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في إنشاء وصف الصور: ' + error.message, type: 'error' });
    } finally {
      setImagePromptsLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!selectedPost) return;

    if (!imageServiceAvailable) {
      setToast({ isVisible: true, message: 'خدمة إنشاء الصور غير مُهيأة (يتطلب HF_TOKEN)', type: 'warning' });
      return;
    }

    setImageGenerating(true);
    try {
      const response = await AIService.generateImage(selectedPost.id, selectedPost.content);

      if (response.success && response.data.imagePath) {
        setGeneratedImage(response.data.imagePath);

        // Update post with image path
        const updatedPost = {
          ...selectedPost,
          image_path: response.data.imagePath
        };

        await PostService.updatePost(selectedPost.id, {
          image_path: response.data.imagePath
        });

        setPosts(posts.map(p => p.id === selectedPost.id ? updatedPost : p));
        setSelectedPost(updatedPost);

        setToast({ isVisible: true, message: 'تم إنشاء الصورة بنجاح!', type: 'success' });
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في إنشاء الصورة: ' + error.message, type: 'error' });
    } finally {
      setImageGenerating(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedImage) return;

    const link = document.createElement('a');
    link.href = getStaticUrl(generatedImage);
    link.download = generatedImage.split('/').pop();
    link.click();
    setToast({ isVisible: true, message: 'تم تحميل الصورة!', type: 'success' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse hashtags if they're stored as JSON string
  const parseHashtags = (hashtags) => {
    if (!hashtags) return [];
    if (Array.isArray(hashtags)) return hashtags;
    try {
      return JSON.parse(hashtags);
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur opacity-20 animate-pulse"></div>
            <div className="relative animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-blue-600"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">جاري تحميل المنشورات...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast
        {...toast}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      <div className="space-y-6">
        {/* Page Header */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
              المنشورات المحفوظة
            </span>
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            إدارة وعرض منشورات لينكد إن والمسودات المحفوظة
          </p>
        </div>

        {/* Search and Filter Bar */}
        {posts.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="البحث في المنشورات..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full ps-10 pe-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-gray-400 hidden sm:block" />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm bg-white min-w-[140px]"
                >
                  <option value="all">كل الوقت</option>
                  <option value="today">اليوم</option>
                  <option value="week">هذا الأسبوع</option>
                  <option value="month">هذا الشهر</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-3 text-sm text-gray-500">
              {searchQuery || dateFilter !== 'all' ? (
                <span>تم العثور على <strong>{filteredPosts.length}</strong> منشور</span>
              ) : (
                <span><strong>{posts.length}</strong> منشور إجمالي</span>
              )}
            </div>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <div className="inline-block p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl mb-6">
                <Eye size={80} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-700 mb-2">لا توجد منشورات محفوظة</h2>
              <p className="text-gray-500 mb-6">أنشئ أول منشور لك لتراه هنا</p>
              <a
                href="/"
                className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl relative overflow-hidden group"
                style={{ background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' }}
              >
                <span className="absolute inset-0 w-full h-full">
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                </span>
                <span className="relative">إنشاء أول منشور</span>
              </a>
            </div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="inline-block p-6 bg-gray-100 rounded-2xl mb-4">
                <Search size={48} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">لم يتم العثور على منشورات</h3>
              <p className="text-gray-500 mb-4">جرب تعديل البحث أو الفلاتر</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                مسح جميع الفلاتر
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Posts List */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-6 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  <h2 className="text-xl font-semibold text-gray-900">منشوراتك</h2>
                </div>
                {paginatedPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`relative bg-white rounded-xl border-2 p-4 sm:p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                      selectedPost?.id === post.id
                        ? 'border-blue-500 shadow-md'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setSelectedPost(post)}
                  >
                    {/* Top gradient line */}
                    <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-xl transition-opacity duration-300 ${
                      selectedPost?.id === post.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`} style={{ background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' }}></div>

                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-gray-900 line-clamp-1 flex-1 me-2 text-sm sm:text-base">
                        {post.title}
                      </h3>
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                        post.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        post.status === 'refined' ? 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700' :
                        'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                      }`}>
                        {post.status}
                      </span>
                    </div>

                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-4 leading-relaxed">
                      {post.content}
                    </p>

                    <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        {formatDate(post.created_at)}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full font-medium">v{post.version}</span>
                    </div>

                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPost(post);
                        }}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                        title="تعديل وتحسين"
                      >
                        <Wand2 size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(post.content, parseHashtags(post.hashtags));
                        }}
                        className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all duration-200"
                        title="نسخ المحتوى"
                      >
                        <Copy size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(post.id);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                        title="حذف المنشور"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>

              {/* Preview Section */}
              <div className="lg:sticky lg:top-24 h-fit">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-6 bg-gradient-to-b from-cyan-600 to-blue-600 rounded-full"></div>
                  <h2 className="text-xl font-semibold text-gray-900">معاينة</h2>
                </div>

                <div className="bg-white rounded-xl border-2 border-gray-200 shadow-md overflow-hidden">
                  {selectedPost ? (
                    <div className="relative">
                      {/* Top gradient bar */}
                      <div className="h-1.5" style={{ background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' }}></div>

                      <div className="p-4 sm:p-6">
                        {/* LinkedIn-style preview */}
                        <div className="border-2 border-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-white to-blue-50/30 shadow-inner mb-5">
                          <div className="flex items-center mb-4">
                            <div className="relative">
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full blur opacity-30"></div>
                              <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                                U
                              </div>
                            </div>
                            <div className="me-3 sm:me-4">
                              <div className="font-bold text-gray-900 text-sm sm:text-base">اسمك</div>
                              <div className="text-xs sm:text-sm text-gray-600">صانع محتوى</div>
                            </div>
                          </div>

                          <div className="whitespace-pre-wrap text-gray-900 leading-relaxed text-sm sm:text-[15px] max-h-48 sm:max-h-64 overflow-y-auto custom-scrollbar">
                            {selectedPost.content}
                          </div>

                          {selectedPost.image_path && (
                            <div className="mt-4">
                              <img
                                src={getStaticUrl(selectedPost.image_path)}
                                alt="Post Visual"
                                className="w-full rounded-lg shadow-md border border-blue-100"
                              />
                            </div>
                          )}

                          {parseHashtags(selectedPost.hashtags).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-blue-100">
                              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {parseHashtags(selectedPost.hashtags).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2.5 py-1 sm:px-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs sm:text-sm font-medium rounded-full shadow-sm"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl mb-5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-xs sm:text-sm text-gray-700">
                              Version <span className="font-semibold text-blue-600">{selectedPost.version}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                            <span className="text-xs sm:text-sm text-gray-700">
                              {formatDate(selectedPost.created_at)}
                            </span>
                          </div>
                          <span className={`flex items-center gap-2 px-2.5 py-1 text-xs sm:text-sm font-medium rounded-full ${
                            selectedPost.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                            selectedPost.status === 'refined' ? 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700' :
                            'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                          }`}>
                            {selectedPost.status}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          <Button
                            onClick={() => handleViewPost(selectedPost)}
                            variant="primary"
                            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                          >
                            <Wand2 size={16} className="me-2" />
                            <span className="hidden sm:inline">تعديل وتحسين</span>
                            <span className="sm:hidden">تعديل</span>
                          </Button>
                          <Button
                            onClick={() => handleCopy(selectedPost.content, parseHashtags(selectedPost.hashtags))}
                            variant="outline"
                          >
                            <Copy size={16} />
                          </Button>
                          <Button
                            onClick={() => {
                              if (confirm('هل أنت متأكد من حذف هذا المنشور؟')) {
                                handleDelete(selectedPost.id);
                              }
                            }}
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 sm:p-12 text-center">
                      <div className="inline-block p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl mb-4">
                        <Eye size={40} className="text-blue-400 sm:w-14 sm:h-14" />
                      </div>
                      <p className="text-gray-500 font-medium">اختر منشوراً للمعاينة</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal for viewing and editing post */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setFeedback('');
          }}
          title={selectedPost?.title}
          size="large"
        >
          {selectedPost && (
            <div className="space-y-6">
              {/* Post Preview with LinkedIn style */}
              <div className="border-2 border-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 bg-gradient-to-br from-white to-blue-50/30 shadow-inner">
                <div className="flex items-center mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full blur opacity-30"></div>
                    <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                      U
                    </div>
                  </div>
                  <div className="me-3 sm:me-4">
                    <div className="font-bold text-gray-900 text-sm sm:text-base">اسمك</div>
                    <div className="text-xs sm:text-sm text-gray-600">صانع محتوى</div>
                  </div>
                </div>

                <div className="whitespace-pre-wrap text-gray-900 leading-relaxed text-sm sm:text-[15px] mb-4">
                  {selectedPost.content}
                </div>

                {parseHashtags(selectedPost.hashtags).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-100">
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {parseHashtags(selectedPost.hashtags).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 sm:px-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs sm:text-sm font-medium rounded-full shadow-sm hover:shadow-md transition-shadow"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-xs sm:text-sm text-gray-700">
                    الإصدار <span className="font-semibold text-blue-600">{selectedPost.version}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                  <span className="text-xs sm:text-sm text-gray-700">
                    تاريخ الإنشاء: {formatDate(selectedPost.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className={`text-xs sm:text-sm font-medium px-2 py-1 rounded-full ${
                    selectedPost.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                    selectedPost.status === 'refined' ? 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700' :
                    'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                  }`}>
                    {selectedPost.status}
                  </span>
                </div>
              </div>

              {/* Improve Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-5 border-2 border-purple-100">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="text-purple-600" size={20} />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                    تحسين هذا المنشور
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  أخبر الذكاء الاصطناعي كيف تريد تحسين هذا المنشور
                </p>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="مثال: اجعله أكثر احترافية وأضف دعوة للتفاعل في النهاية..."
                  className="w-full p-3 sm:p-4 border-2 border-purple-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none transition-all resize-none text-gray-900 text-sm sm:text-base"
                  rows={4}
                  maxLength={500}
                />
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-3 gap-3">
                  <span className="text-xs sm:text-sm text-gray-500">
                    {feedback.length} / 500 حرف
                  </span>
                  <Button
                    onClick={handleImprovePost}
                    loading={isImproving}
                    variant="primary"
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    <Wand2 size={16} className="me-2" />
                    تحسين المنشور
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => handleCopy(selectedPost.content, parseHashtags(selectedPost.hashtags))}
                  variant="primary"
                  className="flex-1"
                >
                  <Copy size={16} className="me-2" />
                  نسخ المنشور
                </Button>

                <ActionMenu align="right">
                  <ActionMenuItem
                    icon={RefreshCw}
                    onClick={handleRegenerateHashtags}
                    loading={isImproving}
                  >
                    إعادة توليد الهاشتاقات
                  </ActionMenuItem>
                  <ActionMenuItem
                    icon={ImageIcon}
                    onClick={() => setShowImageModal(true)}
                  >
                    إضافة صورة
                  </ActionMenuItem>
                  <ActionMenuItem
                    icon={Trash2}
                    variant="danger"
                    onClick={() => {
                      if (confirm('هل أنت متأكد من حذف هذا المنشور؟')) {
                        handleDelete(selectedPost.id);
                        setIsModalOpen(false);
                      }
                    }}
                  >
                    حذف المنشور
                  </ActionMenuItem>
                </ActionMenu>
              </div>
            </div>
          )}
        </Modal>

        {/* Image Generation Modal */}
        <ImageGenerationModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          content={selectedPost?.content}
          imageServiceAvailable={imageServiceAvailable}
          onGeneratePrompts={handleGenerateImagePrompts}
          onGenerateImage={handleGenerateImage}
          promptsLoading={imagePromptsLoading}
          imageGenerating={imageGenerating}
          prompts={imagePrompts}
          generatedImage={generatedImage}
          onDownloadImage={handleDownloadImage}
          toast={setToast}
        />
      </div>
    </>
  );
};

export default SavedPosts;
